import React, { useCallback, useEffect, useState } from 'react';
import { CreateModalPropsType } from './types';
import {
  CreateErrorContainer,
  CreateInputGrid,
  CreateInputTitle,
  CreateModalContent,
  JobTypeSelectContainer,
  ModalButtons,
} from './elements';
import { Button, Select, TextField } from '@mollybet/ui';
import useSelector from 'common/hooks/useSelector';
import {
  CreateJobsBodyData,
  doCreateJobs,
} from 'common/state/jobs/thunkActions';
import { CardTitle, FormError } from 'common/components/cardStyles';
import { JobTypes } from 'common/state/jobs/actions';
import {
  getDaysInMonth,
  getFirstDayOfMonth,
  offsetDateByDays,
} from '../../../common/utils/time';

const capitalise = (text: string): string =>
  text
    .split(' ')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

const JOB_TYPE_OPTIONS = Object.values(JobTypes).map((type) => ({
  value: type,
  label: capitalise(type.replaceAll('_', ' ')),
}));

export const CreateModal = ({
  initialData,
  state,
  dispatch,
  closeModal,
}: CreateModalPropsType) => {
  const [data, _setData] = useState<CreateJobsBodyData>({
    date_from: '',
    date_to: '',
    job_types: [],
    ...initialData,
  });
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    if (!state.pending && !state.error && requestSent) {
      setRequestSent(false);
      closeModal();
    }
  }, [state.pending, state.error, requestSent, closeModal, setRequestSent]);

  const createJobs = useCallback(() => {
    dispatch(doCreateJobs(data));
    setRequestSent(true);
  }, [dispatch, data, setRequestSent]);

  const setData = useCallback(
    (data: CreateJobsBodyData) => {
      const today = new Date().toISOString().slice(0, 10);
      const _data = { ...data };

      if (_data.date_from && _data.date_from > today) _data.date_from = today;
      if (_data.date_to && _data.date_to > today) _data.date_to = today;

      _setData(_data);
    },
    [_setData]
  );

  const setRangeToOneDay = useCallback(() => {
    if (data.date_from) {
      setData({
        ...data,
        date_to: data.date_from,
      });
    } else if (data.date_to) {
      setData({
        ...data,
        date_from: data.date_to,
      });
    }
  }, [data, setData]);

  const setRangeToOneWeek = useCallback(() => {
    if (data.date_from) {
      setData({
        ...data,
        date_to: offsetDateByDays(data.date_from, 6).toISOString().slice(0, 10),
      });
    } else if (data.date_to) {
      setData({
        ...data,
        date_from: offsetDateByDays(data.date_to, -6)
          .toISOString()
          .slice(0, 10),
      });
    }
  }, [data, setData]);

  const setRangeToOneFortnight = useCallback(() => {
    if (data.date_from) {
      setData({
        ...data,
        date_to: offsetDateByDays(data.date_from, 13)
          .toISOString()
          .slice(0, 10),
      });
    } else if (data.date_to) {
      setData({
        ...data,
        date_from: offsetDateByDays(data.date_to, -13)
          .toISOString()
          .slice(0, 10),
      });
    }
  }, [data, setData]);

  const setRangeToOneMonth = useCallback(() => {
    if (data.date_from) {
      setData({
        ...data,
        date_to: offsetDateByDays(
          data.date_from,
          getDaysInMonth(data.date_from) - 1
        )
          .toISOString()
          .slice(0, 10),
      });
    } else if (data.date_to) {
      const prevMonth = offsetDateByDays(getFirstDayOfMonth(data.date_to), -1);
      setData({
        ...data,
        date_from: offsetDateByDays(data.date_to, getDaysInMonth(prevMonth) - 1)
          .toISOString()
          .slice(0, 10),
      });
    }
  }, [data, setData]);

  const { groups } = useSelector((state) => state.auth.user);

  const isScraper = groups.includes("scrapers");
  const isSuperUser = groups.includes("superuser");

  return (
    <CreateModalContent>
      <CardTitle>Create Job</CardTitle>
      <CreateInputGrid>
        <CreateInputTitle>Job Types</CreateInputTitle>
        <JobTypeSelectContainer>
          <Select
            isMulti
            value={data.job_types.map((type) => ({
              value: type,
              label: capitalise(type.replaceAll('_', ' ')),
            }))}
            options={JOB_TYPE_OPTIONS}
            onChange={(options) =>
              setData({
                ...data,
                job_types: options?.map((option) => option.value) || [],
              })
            }
            styles={{ menu: (provided) => ({ ...provided, zIndex: 999 }) }}
          />
        </JobTypeSelectContainer>
        <CreateInputTitle>
          Date Range
          <span style={{ fontSize: '0.75rem' }}> (inclusive)</span>
        </CreateInputTitle>
        <div>
          <TextField
            type="date"
            value={data.date_from}
            onChange={(event) =>
              setData({ ...data, date_from: event.currentTarget.value })
            }
            label="From"
            max={new Date().toISOString().slice(0, 10)}
            shrinked
          />
          <TextField
            type="date"
            value={data.date_to}
            onChange={(event) =>
              setData({ ...data, date_to: event.currentTarget.value })
            }
            label="To"
            max={new Date().toISOString().slice(0, 10)}
            shrinked
          />
          <div style={{ height: '0.5rem' }} />
          <Button
            onClick={setRangeToOneDay}
            disabled={!(data.date_from || data.date_to)}
          >
            Day
          </Button>
          <Button
            onClick={setRangeToOneWeek}
            disabled={!(data.date_from || data.date_to)}
          >
            Week
          </Button>
          <Button
            onClick={setRangeToOneFortnight}
            disabled={!(data.date_from || data.date_to)}
          >
            Fortnight
          </Button>
          <Button
            onClick={setRangeToOneMonth}
            disabled={!(data.date_from || data.date_to)}
          >
            Month
          </Button>
        </div>
      </CreateInputGrid>
      <CreateErrorContainer>
        {data.date_from && data.date_to && data.date_from > data.date_to && (
          <FormError>From date is greater than to date</FormError>
        )}
        {state.error && <FormError>{state.error}</FormError>}
      </CreateErrorContainer>

      <ModalButtons>
        <Button
          variant="primary"
          onClick={createJobs}
          busy={state.pending}
          disabled={
            (!data.date_from ||
            !data.date_to ||
            data.date_from > data.date_to ||
            !data.job_types?.length)
            // Users in the "scrapers" group can create new jobs for the last 30 days.
            || !isScraper
            // Users in BOTH the "scrapers" and "superusers" groups can create jobs for dates more than 30 days ago.
            || (new Date(data.date_from) <= offsetDateByDays(new Date(), -60) && !(isSuperUser && isScraper))
          }
        >
          Create
        </Button>
        <Button onClick={closeModal}>Close</Button>
      </ModalButtons>
    </CreateModalContent>
  );
};
